import { AddIcon } from '@ordr-as/icons'
import { Banner, Button, LoadingWand } from '@ordr-as/umami-react'
import AddFavritAppToLocationMenu from 'components/AddFavritAppToLocationMenu'
import Chip from 'components/Chip'
import ChipGroup from 'components/Chip/ChipGroup'
import DetailTemplate from 'components/DetailTemplate'
import JsonHighlight from 'components/JsonHighlight'
import RemoveAppFromLocationDialog from 'components/RemoveAppFromLocationDialog'
import { compareDesc } from 'date-fns'
import { usePaymentMethodLocationConnections, useDeactivatePaymentMethodForLocation } from 'hooks/usePaymentMethodApi'
import { useTextareaResize } from 'hooks/useTextareaResize'
import { useKeyboardShortcuts } from 'hooks/useKeyboardShortcuts'
import { useEffect, useState, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import ReactTooltip from 'react-tooltip'
import { formatDate } from 'utils/dateUtils'
import { mapFavritAppToName } from 'utils/favritAppUtils'
import {
    FavritApp,
    PaymentMethodLocationDto,
    PaymentProvider,
    UnintegratedFieldsSchemaEntry,
} from '../../../generated-api'

import { useCreatePaymentMethodLocationConnection } from 'hooks/usePaymentMethodApi'
import { useToast } from '@ordr-as/umami-react'

type PaymentMethodLocationConnectionDetailsProps = {
    onClose: () => void
    locations: PaymentMethodLocationDto[]
}

const PaymentMethodLocationConnectionDetails = (props: PaymentMethodLocationConnectionDetailsProps) => {
    const { onClose, locations } = props
    const locationId = useParams().locationId
    const locationName = locations?.find((location) => location.locationId.toString() === locationId)?.locationName

    const { paymentProvider, paymentMethod } = useParams()
    const navigate = useNavigate()
    const toast = useToast()
    const [showChangeHistory, setShowChangeHistory] = useState(false)
    const [showEditPaymentMethodDisplayLabelInput, setShowEditPaymentMethodDisplayLabelInput] = useState(false)
    const [paymentMethodDisplayLabel, setPaymentMethodDisplayLabel] = useState('')
    const [showUnintegratedFieldsSchema, setShowUnintegratedFieldsSchema] = useState(false)
    const [unintegratedFieldsSchema, setUnintegratedFieldsSchema] = useState<
        UnintegratedFieldsSchemaEntry[] | undefined
    >(undefined)
    const [unintegratedFieldsSchemaText, setUnintegratedFieldsSchemaText] = useState<string>('')
    const inputRef = useRef<HTMLInputElement>(null)
    const textareaRef = useTextareaResize(unintegratedFieldsSchemaText, showUnintegratedFieldsSchema)

    const { mutate: createPaymentMethodLocationConnection } = useCreatePaymentMethodLocationConnection(
        paymentProvider as PaymentProvider,
        paymentMethod as string,
        Number(locationId)
    )

    const { mutate: deactivatePaymentMethodForLocation, isLoading: isDeactivating } =
        useDeactivatePaymentMethodForLocation(
            paymentProvider as PaymentProvider,
            paymentMethod as string,
            Number(locationId)
        )

    if (!paymentProvider || !paymentMethod || !locationId) {
        throw new Error('Payment provider, payment method and location ID must be present in path.')
    }

    const [favritAppToRemove, setFavritAppToRemove] = useState<FavritApp | undefined>(undefined)

    const {
        data: locationConnections,
        isLoading,
        isSuccess,
        isError,
        error,
    } = usePaymentMethodLocationConnections(
        paymentProvider as PaymentProvider,
        paymentMethod as string,
        Number(locationId)
    )

    const activeConnection = locationConnections?.find((lc) => lc.isActive)

    const sortedLocationConnections = [...(locationConnections || [])].sort((a, b) =>
        compareDesc(a.activeFrom, b.activeFrom)
    )

    useEffect(() => {
        if (activeConnection) {
            setPaymentMethodDisplayLabel(activeConnection.overridableConfiguration?.paymentMethodDisplayLabel || '')
            setUnintegratedFieldsSchema(activeConnection.overridableConfiguration?.unintegratedFieldsSchema)
            setUnintegratedFieldsSchemaText(
                activeConnection.overridableConfiguration?.unintegratedFieldsSchema
                    ? JSON.stringify(activeConnection.overridableConfiguration.unintegratedFieldsSchema, null, 2)
                    : ''
            )
        }
    }, [activeConnection])

    useEffect(() => {
        if (showEditPaymentMethodDisplayLabelInput && inputRef.current) {
            inputRef.current.focus()
        }
    }, [showEditPaymentMethodDisplayLabelInput])

    const editPaymentMethodDisplayLabel = useCallback(() => {
        setShowEditPaymentMethodDisplayLabelInput(true)
    }, [])

    const savePaymentMethodDisplayLabel = useCallback(() => {
        setShowEditPaymentMethodDisplayLabelInput(false)

        if (paymentMethodDisplayLabel !== activeConnection?.overridableConfiguration?.paymentMethodDisplayLabel) {
            createPaymentMethodLocationConnection({
                locationId: Number(locationId),
                enabledInApps: activeConnection?.enabledInApps || new Set(),
                overridableConfiguration: {
                    paymentMethodDisplayLabel: paymentMethodDisplayLabel,
                    unintegratedFieldsSchema: activeConnection?.overridableConfiguration?.unintegratedFieldsSchema,
                },
            })
        }
    }, [paymentMethodDisplayLabel, activeConnection, createPaymentMethodLocationConnection, locationId])

    const editUnintegratedFieldsSchema = () => {
        setShowUnintegratedFieldsSchema(true)
    }

    const saveUnintegratedFieldsSchema = () => {
        setShowUnintegratedFieldsSchema(false)

        if (unintegratedFieldsSchema !== activeConnection?.overridableConfiguration?.unintegratedFieldsSchema) {
            createPaymentMethodLocationConnection({
                locationId: Number(locationId),
                enabledInApps: activeConnection?.enabledInApps || new Set(),
                overridableConfiguration: {
                    unintegratedFieldsSchema: unintegratedFieldsSchema,
                    paymentMethodDisplayLabel: activeConnection?.overridableConfiguration?.paymentMethodDisplayLabel,
                },
            })
        }
    }

    const handlePaymentMethodDisplayLabelEscape = useCallback(() => {
        setShowEditPaymentMethodDisplayLabelInput(false)
        setPaymentMethodDisplayLabel(activeConnection?.overridableConfiguration?.paymentMethodDisplayLabel || '')
    }, [activeConnection])

    useKeyboardShortcuts(
        showEditPaymentMethodDisplayLabelInput,
        handlePaymentMethodDisplayLabelEscape,
        savePaymentMethodDisplayLabel
    )

    const handleUnintegratedFieldsSchemaEscape = useCallback(() => {
        setShowUnintegratedFieldsSchema(false)
        setUnintegratedFieldsSchema(activeConnection?.overridableConfiguration?.unintegratedFieldsSchema)
        setUnintegratedFieldsSchemaText(
            activeConnection?.overridableConfiguration?.unintegratedFieldsSchema
                ? JSON.stringify(activeConnection.overridableConfiguration.unintegratedFieldsSchema, null, 2)
                : ''
        )
    }, [activeConnection])

    useKeyboardShortcuts(showUnintegratedFieldsSchema, handleUnintegratedFieldsSchemaEscape)

    const handleRemoveLocation = () => {
        if (!window.confirm(`Are you sure you want to remove ${locationName} (${locationId}) from this payment method?`)) {
            return
        }

        deactivatePaymentMethodForLocation(undefined, {
            onSuccess: () => {
                toast.pushSuccess({
                    id: 'deactivate-location-success',
                    message: `Successfully removed ${locationName} from payment method`,
                })
                navigate(`/payment-methods/${paymentProvider}/${paymentMethod}/locations`)
            },
            onError: (error) => {
                console.error('Failed to remove location:', error)
                toast.pushError({
                    id: 'deactivate-location-error',
                    message: 'Failed to remove location from payment method',
                })
            },
        })
    }

    return (
        <DetailTemplate
            heading={`${locationName} (${locationId})`}
            closeButtonTooltip="Close location settings"
            onClose={onClose}
        >
            {isLoading && <LoadingWand className="mx-auto" />}

            {isError && (
                <Banner kind="error" onClose={() => null} stretch="parent" className="my-3">
                    {(error as Error).message}
                </Banner>
            )}

            {isSuccess && (
                <div>
                    {!activeConnection && (
                        <Banner className="mb-5" kind="warn" stretch="parent" onClose={() => null}>
                            This location has no active configuration for the payment method
                        </Banner>
                    )}

                    {activeConnection && (
                        <div>
                            <div className="mb-5">
                                <h4 className="mb-1">Enabled in</h4>
                                <div className="flex">
                                    {activeConnection.enabledInApps ? (
                                        Array.from(activeConnection.enabledInApps).map((app) => (
                                            <Chip
                                                key={`${activeConnection.locationId}-${app}`}
                                                size="medium"
                                                text={mapFavritAppToName(app)}
                                                closeButtonTooltip="Remove Favrit App"
                                                onClose={() => setFavritAppToRemove(app)}
                                            />
                                        ))
                                    ) : (
                                        <div className="text-grey-dark">No override</div>
                                    )}

                                    {activeConnection.enabledInApps?.size === 0 && (
                                        <div className="flex flex-col justify-center text-sm text-grey-dark mr-2">
                                            <div>None</div>
                                        </div>
                                    )}

                                    {activeConnection.enabledInApps &&
                                        activeConnection.enabledInApps.size < Object.values(FavritApp).length && (
                                            <AddFavritAppToLocationMenu
                                                locationConnection={activeConnection}
                                                buttonElement={
                                                    <div
                                                        data-tip
                                                        data-for="add-favrit-app-to-location-button-tooltip"
                                                        className="flex flex-col justify-center items-center w-[30px] min-h-[28px] rounded cursor-pointer hover:bg-purple-lightest"
                                                    >
                                                        <AddIcon height={17} width={17} />
                                                    </div>
                                                }
                                            />
                                        )}

                                    <ReactTooltip
                                        id="add-favrit-app-to-location-button-tooltip"
                                        arrowColor="transparent"
                                        effect="solid"
                                        padding="1px 4px"
                                        className="text-bold !opacity-100"
                                    >
                                        Add Favrit App
                                    </ReactTooltip>
                                </div>
                            </div>

                            {favritAppToRemove && (
                                <RemoveAppFromLocationDialog
                                    locationConnection={activeConnection}
                                    favritApp={favritAppToRemove}
                                    dialogIsOpen={favritAppToRemove !== undefined}
                                    onClose={() => setFavritAppToRemove(undefined)}
                                />
                            )}

                            <div className="mb-5">
                                <h4 className="mb-1">Payment method display label</h4>
                                <div className="flex items-center">
                                    {showEditPaymentMethodDisplayLabelInput ? (
                                        <div className="flex items-center">
                                            <input
                                                ref={inputRef}
                                                value={paymentMethodDisplayLabel}
                                                onChange={(e) => setPaymentMethodDisplayLabel(e.target.value)}
                                                className="border border-grey-light rounded-md p-2"
                                            />
                                            <Button
                                                onClick={() => savePaymentMethodDisplayLabel()}
                                                kind="secondary"
                                                size="small"
                                                className="ml-2"
                                            >
                                                Save
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center">
                                            {paymentMethodDisplayLabel || (
                                                <div className="text-grey-dark">No override</div>
                                            )}
                                            <Button
                                                onClick={() => editPaymentMethodDisplayLabel()}
                                                kind="secondary"
                                                size="small"
                                                className="ml-2"
                                            >
                                                Edit
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="mb-5">
                                <h4 className="mb-1">Unintegrated fields schema</h4>
                                {showUnintegratedFieldsSchema ? (
                                    <div className="flex items-center">
                                        <textarea
                                            ref={textareaRef}
                                            value={unintegratedFieldsSchemaText}
                                            onChange={(e) => {
                                                setUnintegratedFieldsSchemaText(e.target.value)
                                                try {
                                                    const parsed = JSON.parse(e.target.value)
                                                    setUnintegratedFieldsSchema(parsed)
                                                } catch (error) {
                                                    // Ignore parsing errors while user is typing
                                                }
                                            }}
                                            className="border border-grey-light rounded-md p-2 w-full min-h-[100px] resize-none"
                                        />
                                        <Button
                                            onClick={() => saveUnintegratedFieldsSchema()}
                                            kind="secondary"
                                            size="small"
                                            className="ml-2"
                                        >
                                            Save
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="flex items-center">
                                        {unintegratedFieldsSchema ? (
                                            <JsonHighlight object={unintegratedFieldsSchema} />
                                        ) : (
                                            <div className="text-grey-dark">No override</div>
                                        )}
                                        <Button
                                            onClick={() => editUnintegratedFieldsSchema()}
                                            kind="secondary"
                                            size="small"
                                            className="ml-2"
                                        >
                                            Edit
                                        </Button>
                                    </div>
                                )}
                            </div>

                            <div className="mb-5">
                                <Button
                                    kind="secondary"
                                    size="small"
                                    onClick={handleRemoveLocation}
                                    isDisabled={isDeactivating}
                                >
                                    {isDeactivating ? 'Removing...' : 'Remove Location'}
                                </Button>
                            </div>
                        </div>
                    )}

                    <div>
                        <h4 className="mb-1">Change history</h4>

                        {
                            <Button
                                kind="secondary"
                                size="small"
                                onClick={() => setShowChangeHistory(!showChangeHistory)}
                            >
                                {showChangeHistory ? 'Hide changes' : 'Show changes'}
                            </Button>
                        }

                        <div className="divide-y divide-[#e2e8f0]">
                            {showChangeHistory &&
                                sortedLocationConnections?.map((lc) => (
                                    <div key={lc.externalId} className="flex flex-col pb-4 pt-4">
                                        <div className="flex mb-3">
                                            <div className="flex flex-col mr-5">
                                                <div className="text-xs text-grey-dark mb-1">
                                                    Payment method display label was
                                                </div>

                                                <div>
                                                    {lc.overridableConfiguration?.paymentMethodDisplayLabel || (
                                                        <div className="text-grey-dark">No override</div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="mr-5">
                                                {lc.enabledInApps ? (
                                                    <ChipGroup
                                                        heading="Enabled in"
                                                        showEmptyPlaceholder={lc.enabledInApps.size === 0}
                                                    >
                                                        {Array.from(lc.enabledInApps).map((app) => (
                                                            <Chip
                                                                key={`${lc.locationId}-${app}`}
                                                                size="small"
                                                                text={mapFavritAppToName(app)}
                                                            />
                                                        ))}
                                                    </ChipGroup>
                                                ) : (
                                                    <div className="text-grey-dark"></div>
                                                )}
                                            </div>

                                            <div className="flex flex-col">
                                                <div className="text-xs text-grey-dark mb-1">
                                                    Unintegrated fields schema was
                                                </div>

                                                {lc.overridableConfiguration?.unintegratedFieldsSchema ? (
                                                    <JsonHighlight
                                                        object={lc.overridableConfiguration.unintegratedFieldsSchema}
                                                        className="text-xs"
                                                    />
                                                ) : (
                                                    <div className="text-grey-dark">No override</div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="text-sm mb-1">
                                            Config active from {formatDate(lc.activeFrom)} to{' '}
                                            {lc.activeTo ? formatDate(lc.activeTo) : 'eternity'}
                                        </div>
                                        <div className="text-xs text-grey-dark mb-1">
                                            Config created by {lc.createdByUserEmail} at {formatDate(lc.created)}
                                        </div>
                                        <div className="text-xs text-grey-dark mb-1">
                                            Config updated by {lc.updatedByUserEmail} at {formatDate(lc.updated)}{' '}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
            )}
        </DetailTemplate>
    )
}

export default PaymentMethodLocationConnectionDetails
